import { Button, Stack, StackProps, useTheme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Label } from 'src/components/Fields';
import { MAX_LABELS_TO_SHOW } from 'src/components/Fields/LabelField';
import { SelectMenu } from 'src/components/SelectMenu';
import { SelectMenuOption } from 'src/components/SelectMenu/select-menu.types';
import { ShowMoreButton } from 'src/components/ShowMoreButton/show-more-button';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { NON_LABEL_GROUP_ID } from 'src/constants/label';
import {
  Address,
  CtaLabel,
  LABEL_PICKER_PAGE_SIZE,
  onlyPublicsFilter,
  useGroupRules,
  useLabels,
  useLabelToCta,
  UserGroupRules,
} from 'src/graphql';
import { ConditionInput } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { useDebouncedValue } from 'src/utils/hooks/use-debounced-value';
import { pickPastelColor } from 'src/utils/strings';

const CLIENT_TO_ADDRESS_GROUP_FILTER: ConditionInput = {
  attribute: 'labelGroup/displayName',
  operator: '=',
  value: 'ClientToAddress',
};

export const ClientLabel = ({
  clientId,
  isDeleted = false,
  address,
  partnerId,
  labels,
  isEditable = true,
  ...props
}: StackProps & {
  clientId: string;
  /** A soft-deleted client must not have labels added or removed. */
  isDeleted?: boolean;
  labels?: CtaLabel[];
  isEditable?: boolean;
  partnerId?: number;
  address?: Partial<Address>;
}) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_DELAY);

  const { showSuccessAlert } = useNotifications();

  const { groupRules } = useGroupRules();
  const canAccessToPrivateLabels = Boolean(groupRules[UserGroupRules.AccessToPrivateLabels]);

  const [isOpen, setIsOpen] = useState(false);

  // Derive filters fresh each render — do not append onto prior state when groupRules change.
  const filters = useMemo<ConditionInput[]>(() => {
    const next: ConditionInput[] = [CLIENT_TO_ADDRESS_GROUP_FILTER];

    if (!canAccessToPrivateLabels) {
      next.push(onlyPublicsFilter);
    }

    if (debouncedSearch) {
      next.push({
        attribute: 'displayName',
        operator: 'ilike',
        value: `%${debouncedSearch}%`,
      });
    }

    return next;
  }, [canAccessToPrivateLabels, debouncedSearch]);

  const {
    labels: labelRecords,
    loading,
    hasMore,
    loadMore,
    isLoadingMore,
  } = useLabels({ partnerId }, { filters, skip: !groupRules, limit: LABEL_PICKER_PAGE_SIZE });

  const { assignLabelToCta, deleteLabelToCta } = useLabelToCta();

  const options = useMemo<SelectMenuOption[]>(
    () =>
      labelRecords.map((label) => ({
        value: label.labelId,
        title: label.displayName ?? '',
      })),
    [labelRecords],
  );

  const values = useMemo(() => (labels ?? []).map((label) => label.labelId), [labels]);

  const onValuesChange = useCallback(
    async (_: string[], option: SelectMenuOption) => {
      if (isDeleted) {
        return;
      }

      const labelData = {
        labelId: option.value,
        clientId,
        addressId: address?.addressId!,
      };

      if ((labels ?? []).some((label) => label.labelId === option.value)) {
        const deletedLabels = await deleteLabelToCta(labelData);
        if (deletedLabels?.length) {
          showSuccessAlert('Label removed');
        }
        return;
      }

      const createdLabel = await assignLabelToCta({
        ...labelData,
        partnerId: selectedPartnerId ?? partnerId ?? 0,
      });

      if (createdLabel) {
        showSuccessAlert('Updated labels');
      }
    },
    [
      address?.addressId,
      clientId,
      labels,
      assignLabelToCta,
      deleteLabelToCta,
      isDeleted,
      partnerId,
      selectedPartnerId,
      showSuccessAlert,
    ],
  );

  const theme = useTheme();

  const remainingElements = useMemo(
    () => (labels?.length ?? 0) - MAX_LABELS_TO_SHOW,
    [labels?.length],
  );

  const showShowMoreButton = useMemo(
    () => (labels?.length ?? 0) > MAX_LABELS_TO_SHOW,
    [labels?.length],
  );

  return (
    <Stack flexWrap="wrap" direction="row" gap={1} {...props}>
      {labels
        ?.slice(0, isOpen ? labels?.length : MAX_LABELS_TO_SHOW)
        .map((label) => (
          <Label
            key={label.labelId + label.addressId + label.clientId}
            label={label.labelName}
            backgroundColor={pickPastelColor(
              label?.labelGroupId ?? NON_LABEL_GROUP_ID,
              theme.palette.mode,
            )}
            borderRadius={1}
          />
        ))}
      {isEditable ? (
        <>
          <ShowMoreButton
            showShowMoreButton={showShowMoreButton}
            sx={{
              color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
              fontSize: '0.725rem',
              width: 'fit-content',
            }}
            onClick={() => setIsOpen((currentValue) => !currentValue)}
            text={isOpen ? 'Show Less' : `Show ${remainingElements} More`}
          />

          {!isDeleted && (
            // Single label group with group rows skipped, so the flat SelectMenu
            // is enough — no nesting to browse here.
            <SelectMenu
              label="Labels"
              multiple
              options={options}
              values={values}
              onValuesChange={onValuesChange}
              search
              searchPlaceholder="Add labels…"
              searchValue={search}
              onSearchChange={setSearch}
              loading={loading}
              hasMore={hasMore}
              loadingMore={isLoadingMore}
              onLoadMore={loadMore}
              renderTrigger={({ getTriggerProps }) => (
                <Button
                  {...getTriggerProps()}
                  sx={{
                    fontSize: '0.725rem',
                    width: 'fit-content',
                  }}
                >
                  + Label
                </Button>
              )}
            />
          )}
        </>
      ) : null}
    </Stack>
  );
};
